/**
 * src/services/spatial/spatialVisitorId.ts
 *
 * Who a visitor is when they have not connected a wallet.
 *
 * Asking a question of a building should not require signing anything,
 * so most people talking to a spatial object are anonymous. But
 * anonymous cannot mean *indistinguishable*: the proximity tracker keys
 * its hysteresis per participant, and the server's per-visitor message
 * quota is only a quota if one visitor is one participant. Until now
 * every anonymous visitor was the literal string `map-viewer`, which
 * made them all the same person — one visitor's arrival cancelled
 * another's, and any per-visitor limit would have been a global one.
 *
 * So: a random id, generated once and kept in `localStorage`, following
 * the same convention as the flag intent cache. It is deliberately not
 * derived from anything about the visitor — not an IP, not a
 * fingerprint, not a wallet. It identifies a browser to a rate limiter
 * and carries no claim about who is holding it, which is why it is safe
 * to keep and useless to correlate.
 *
 * It never reaches the AI. `userContext` sent to GeoLink carries the
 * object's centroid and nothing else; this id stays between the browser
 * and our own session bookkeeping.
 */

/** Bumped with the stored shape. */
export const VISITOR_ID_KEY = 'geotrust:spatial-visitor-v1';

/**
 * `visitor-` plus a v4 UUID.
 *
 * The prefix is for the humans reading logs: an id that is obviously
 * not a Stellar address cannot be mistaken for one. Nothing decides
 * anything from it — the server reads the presence of a wallet, not the
 * shape of an id, when it chooses which quota applies.
 */
const SHAPE = /^visitor-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

/**
 * Survives a browser that refuses storage entirely — Safari in private
 * mode throws on write rather than returning null — so a visitor there
 * is still one participant for the lifetime of the tab instead of
 * regenerating on every call.
 */
let memoryId: string | null = null;

function randomUuid(): string {
  const c = typeof crypto !== 'undefined' ? crypto : undefined;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();

  // Older Safari has getRandomValues but not randomUUID.
  if (c && typeof c.getRandomValues === 'function') {
    const bytes = c.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return [
      hex.slice(0, 8), hex.slice(8, 12), hex.slice(12, 16), hex.slice(16, 20), hex.slice(20),
    ].join('-');
  }

  // No CSPRNG at all. Uniqueness is all that is needed of this value —
  // it authorizes nothing — so a weak source is worse than the others
  // and still adequate.
  const weak = () => Math.floor(Math.random() * 0x10000).toString(16).padStart(4, '0');
  return `${weak()}${weak()}-${weak()}-4${weak().slice(1)}-a${weak().slice(1)}-${weak()}${weak()}${weak()}`;
}

/**
 * This browser's stable anonymous visitor id, creating one on first ask.
 *
 * A stored value that does not match the expected shape is replaced
 * rather than trusted: it is either from an older version or something
 * a page put there, and both are better off regenerated than sent to a
 * server that will key a quota on them.
 */
export function spatialVisitorId(): string {
  try {
    const stored = localStorage.getItem(VISITOR_ID_KEY);
    if (stored && SHAPE.test(stored)) {
      memoryId = stored;
      return stored;
    }
    const fresh = memoryId && SHAPE.test(memoryId) ? memoryId : `visitor-${randomUuid()}`;
    localStorage.setItem(VISITOR_ID_KEY, fresh);
    memoryId = fresh;
    return fresh;
  } catch {
    if (!memoryId) memoryId = `visitor-${randomUuid()}`;
    return memoryId;
  }
}

/**
 * The id to send as the participant: the wallet when one is connected,
 * otherwise the anonymous id.
 *
 * One helper so every surface agrees. A visitor who chats from the map
 * tooltip and listens from the nearby panel is one participant to the
 * object, not two, and connecting a wallet mid-visit changes which
 * quota applies rather than splitting them into strangers.
 */
export function spatialParticipantId(wallet?: string | null): string {
  const trimmed = typeof wallet === 'string' ? wallet.trim() : '';
  return trimmed || spatialVisitorId();
}

/** Test hook: forget the in-memory copy. */
export function _resetVisitorIdForTests(): void {
  memoryId = null;
}
